// UserReportsView — admin de plataforma faz triagem de bug reports e feature
// requests dos usuários. Lista filtrável + drawer de detalhe com form de
// resposta + ajuste de status/severidade.
//
// Endpoints: GET/PATCH/POST /api/manager/user-reports

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  AlertTriangle,
  Bug,
  CheckCircle2,
  Clock,
  Filter,
  Inbox,
  LifeBuoy,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCw,
  ScrollText,
  Search,
  Send,
  Star,
  User as UserIcon,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type AdminUserReport } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type AdminReportKind = AdminUserReport["kind"];

const KIND_ICON: Record<AdminReportKind, typeof Bug> = {
  bug: Bug,
  feature: Lightbulb,
  bank_request: ScrollText,
  feedback: Star,
  suporte: LifeBuoy
};
const KIND_COLOR: Record<AdminReportKind, string> = {
  bug: "text-red-600 dark:text-red-400",
  feature: "text-amber-600 dark:text-amber-400",
  bank_request: "text-sky-600 dark:text-sky-400",
  feedback: "text-violet-600 dark:text-violet-400",
  suporte: "text-emerald-600 dark:text-emerald-400"
};

type KindFilter = "all" | "bug" | "feature" | "bank_request" | "feedback" | "suporte";
type StatusFilter =
  | "all"
  | "open"
  | "in_progress"
  | "resolved"
  | "wontfix";

interface ListState {
  phase: "idle" | "loading" | "ready" | "error";
  list: AdminUserReport[];
  errMsg: string | null;
  lastLoadedAt: number;
}

const listState = signal<ListState>({
  phase: "idle",
  list: [],
  errMsg: null,
  lastLoadedAt: 0
});

const filterKind = signal<KindFilter>("all");
const filterStatus = signal<StatusFilter>("open");
const filterQuery = signal<string>("");
const openId = signal<string | null>(null);

function matchesQuery(r: AdminUserReport, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const haystack = [
    r.title,
    r.description ?? "",
    r.reporterName ?? "",
    r.reporterEmail ?? "",
    r.companyName ?? "",
    r.adminResponse ?? "",
    r.id
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

async function loadList() {
  listState.value = { ...listState.value, phase: "loading", errMsg: null };
  try {
    const res = await api.listUserReports({
      kind: filterKind.value === "all" ? undefined : filterKind.value,
      status: filterStatus.value === "all" ? undefined : filterStatus.value
    });
    listState.value = {
      phase: "ready",
      list: res.reports,
      errMsg: null,
      lastLoadedAt: Date.now()
    };
  } catch (err) {
    listState.value = {
      ...listState.value,
      phase: "error",
      errMsg: friendlyError(err)
    };
  }
}

const STATUS_LABEL: Record<AdminUserReport["status"], string> = {
  open: "Aberto",
  in_progress: "Em andamento",
  resolved: "Resolvido",
  wontfix: "Não será feito"
};

const STATUS_BADGE: Record<AdminUserReport["status"], string> = {
  open: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  in_progress:
    "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  resolved:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  wontfix:
    "bg-muted/40 text-muted-foreground border-border"
};

const SEVERITY_BADGE: Record<NonNullable<AdminUserReport["severity"]>, string> =
  {
    low: "bg-muted/40 text-muted-foreground border-border",
    medium:
      "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    high: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
    critical:
      "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30"
  };

export function UserReportsView() {
  useEffect(() => {
    if (listState.value.phase === "idle") void loadList();
  }, []);

  const state = listState.value;
  const query = filterQuery.value.trim();
  const filteredList = state.list.filter((r) => matchesQuery(r, query));
  const opened = openId.value
    ? state.list.find((r) => r.id === openId.value) ?? null
    : null;

  return (
    <div class="space-y-4">
      <FilterBar />

      {state.phase === "loading" && (
        <div class="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div class="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
            <div>
              <h3 class="text-sm font-bold">Falha ao carregar reports</h3>
              {state.errMsg && (
                <p class="text-xs text-muted-foreground">{state.errMsg}</p>
              )}
              <button
                type="button"
                onClick={() => void loadList()}
                class="mt-1 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      )}

      {state.phase === "ready" && state.list.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-12 text-center">
          <Inbox className="mx-auto mb-2 size-8 text-muted-foreground/50" />
          <h3 class="text-sm font-bold">Nada por aqui</h3>
          <p class="mt-1 text-xs text-muted-foreground">
            Não há reports com os filtros atuais.
          </p>
        </div>
      )}

      {state.phase === "ready" &&
        state.list.length > 0 &&
        filteredList.length === 0 && (
          <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-10 text-center">
            <Search className="mx-auto mb-2 size-6 text-muted-foreground/50" />
            <h3 class="text-sm font-bold">Nenhum resultado para "{query}"</h3>
            <p class="mt-1 text-xs text-muted-foreground">
              Tente outras palavras ou limpe a busca.
            </p>
            <button
              type="button"
              onClick={() => {
                filterQuery.value = "";
              }}
              class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
            >
              Limpar busca
            </button>
          </div>
        )}

      {state.phase === "ready" && filteredList.length > 0 && (
        <ul class="space-y-2">
          {filteredList.map((r) => (
            <ReportRow
              key={r.id}
              report={r}
              onOpen={() => {
                openId.value = r.id;
              }}
            />
          ))}
        </ul>
      )}

      {opened && (
        <DetailDrawer
          report={opened}
          onClose={() => {
            openId.value = null;
          }}
          onUpdated={(updated) => {
            listState.value = {
              ...listState.value,
              list: listState.value.list.map((r) =>
                r.id === updated.id ? updated : r
              )
            };
          }}
        />
      )}
    </div>
  );
}

function FilterBar() {
  const query = filterQuery.value;
  return (
    <div class="space-y-2 rounded-md border border-border bg-card/40 px-3 py-2">
      <div class="flex flex-wrap items-center gap-2">
        <Filter className="size-4 text-muted-foreground" />

        <FilterGroup
          label="Tipo"
          value={filterKind.value}
          options={[
            { v: "all", label: "Todos" },
            { v: "suporte", label: "Suporte" },
            { v: "bug", label: "Bugs" },
            { v: "feature", label: "Sugestões" },
            { v: "bank_request", label: "Pedidos de banco" },
            { v: "feedback", label: "Feedbacks" }
          ]}
          onChange={(v) => {
            filterKind.value = v as KindFilter;
            void loadList();
          }}
        />

        <FilterGroup
          label="Status"
          value={filterStatus.value}
          options={[
            { v: "all", label: "Todos" },
            { v: "open", label: "Aberto" },
            { v: "in_progress", label: "Em andamento" },
            { v: "resolved", label: "Resolvido" },
            { v: "wontfix", label: "Não será feito" }
          ]}
          onChange={(v) => {
            filterStatus.value = v as StatusFilter;
            void loadList();
          }}
        />

        <button
          type="button"
          onClick={() => void loadList()}
          class="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold hover:bg-accent"
          title="Atualizar"
        >
          <RefreshCw width="1em" height="1em" />
          Atualizar
        </button>
      </div>

      <div class="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onInput={(e) => {
            filterQuery.value = (e.target as HTMLInputElement).value;
          }}
          placeholder="Buscar por título, descrição, usuário, email, organização ou ID…"
          class="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-7 text-xs outline-none focus:border-primary"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              filterQuery.value = "";
            }}
            aria-label="Limpar busca"
            class="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function FilterGroup({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: { v: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div class="flex items-center gap-1">
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div class="flex flex-wrap gap-0.5 rounded-md bg-background/60 p-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            class={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors ${
              value === o.v
                ? "bg-primary text-white"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReportRow({
  report,
  onOpen
}: {
  report: AdminUserReport;
  onOpen: () => void;
}) {
  const Icon = KIND_ICON[report.kind] ?? Bug;
  const iconColor = KIND_COLOR[report.kind] ?? "text-muted-foreground";

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        class="flex w-full items-start gap-3 rounded-md border border-border bg-card/40 px-4 py-3 text-left hover:border-primary/40 hover:bg-accent/40"
      >
        <Icon className={`mt-0.5 size-5 shrink-0 ${iconColor}`} />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-start justify-between gap-2">
            <h3 class="truncate text-sm font-bold">{report.title}</h3>
            <div class="flex shrink-0 flex-wrap items-center gap-1">
              {report.severity && (
                <span
                  class={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${SEVERITY_BADGE[report.severity]}`}
                >
                  {report.severity}
                </span>
              )}
              <span
                class={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_BADGE[report.status]}`}
              >
                {STATUS_LABEL[report.status]}
              </span>
            </div>
          </div>
          {report.description && (
            <p class="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {report.description}
            </p>
          )}
          <div class="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground/80">
            <span class="inline-flex items-center gap-0.5">
              <UserIcon width="11" height="11" />
              {report.reporterName ?? "—"}{" "}
              <span class="opacity-70">({report.reporterEmail ?? "—"})</span>
            </span>
            {report.companyName && (
              <span class="rounded bg-accent px-1.5 py-0.5">
                {report.companyName}
              </span>
            )}
            <span class="inline-flex items-center gap-0.5">
              <Clock width="11" height="11" />
              {formatDate(report.createdAt)}
            </span>
            {report.kind === "feedback" && report.rating && (
              <span class="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    width="11"
                    height="11"
                    className={
                      i < (report.rating ?? 0)
                        ? "fill-amber-400 text-amber-500"
                        : "text-muted-foreground/30"
                    }
                  />
                ))}
              </span>
            )}
            {report.adminResponse && (
              <span class="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 width="11" height="11" />
                Respondido
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function DetailDrawer({
  report,
  onClose,
  onUpdated
}: {
  report: AdminUserReport;
  onClose: () => void;
  onUpdated: (r: AdminUserReport) => void;
}) {
  const [responseText, setResponseText] = useState(report.adminResponse ?? "");
  const [newStatus, setNewStatus] = useState<AdminUserReport["status"]>(
    report.status === "open" ? "resolved" : report.status
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function saveSeverity(sev: AdminUserReport["severity"]) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.updateUserReport(report.id, { severity: sev });
      onUpdated(res.report);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveStatusOnly(status: AdminUserReport["status"]) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.updateUserReport(report.id, { status });
      onUpdated(res.report);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitResponse() {
    if (!responseText.trim()) {
      setErr("Texto da resposta vazio.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await api.respondUserReport(report.id, {
        text: responseText.trim(),
        status: newStatus
      });
      onUpdated(res.report);
      void openAlert({
        title: "Resposta enviada",
        message: "O usuário foi notificado e a resposta está visível pra ele."
      });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Erro inesperado.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  const Icon = KIND_ICON[report.kind] ?? Bug;
  const iconColor = KIND_COLOR[report.kind] ?? "text-muted-foreground";

  return (
    <div
      class="fixed inset-0 z-[200] flex bg-black/50"
      /* Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]] */
    >
      <div
        class="ml-auto flex h-full w-full max-w-2xl flex-col bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start gap-3 border-b border-border px-5 py-4">
          <Icon className={`mt-0.5 size-6 shrink-0 ${iconColor}`} />
          <div class="min-w-0 flex-1">
            <h2 class="truncate text-lg font-bold">{report.title}</h2>
            <p class="mt-0.5 text-xs text-muted-foreground">
              {report.reporterName ?? "—"} ·{" "}
              <span class="font-mono">{report.reporterEmail ?? "—"}</span>
              {report.companyName && (
                <>
                  {" · "}
                  <span>{report.companyName}</span>
                </>
              )}
              {" · "}
              {formatDate(report.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Fechar"
            class="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <X className="size-5" />
          </button>
        </header>

        <div class="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <Section title="Descrição do usuário">
            {report.description ? (
              <p class="whitespace-pre-line text-sm leading-relaxed text-foreground">
                {report.description}
              </p>
            ) : (
              <p class="text-xs italic text-muted-foreground">
                Sem descrição.
              </p>
            )}
          </Section>

          <Section title="Status e severidade">
            <div class="space-y-3">
              <FieldRow label="Status atual">
                <PillSelect
                  value={report.status}
                  options={[
                    { v: "open", label: "Aberto" },
                    { v: "in_progress", label: "Em andamento" },
                    { v: "resolved", label: "Resolvido" },
                    { v: "wontfix", label: "Não será feito" }
                  ]}
                  disabled={busy}
                  onChange={(v) =>
                    void saveStatusOnly(v as AdminUserReport["status"])
                  }
                />
              </FieldRow>
              <FieldRow label="Severidade">
                <PillSelect
                  value={report.severity ?? "_none"}
                  options={[
                    { v: "_none", label: "—" },
                    { v: "low", label: "Baixa" },
                    { v: "medium", label: "Média" },
                    { v: "high", label: "Alta" },
                    { v: "critical", label: "Crítica" }
                  ]}
                  disabled={busy}
                  onChange={(v) => {
                    const sev =
                      v === "_none"
                        ? null
                        : (v as NonNullable<AdminUserReport["severity"]>);
                    void saveSeverity(sev);
                  }}
                />
              </FieldRow>
            </div>
          </Section>

          {report.context && (
            <Section title="Contexto técnico">
              <ContextBlock context={report.context} />
            </Section>
          )}

          <Section
            title={
              report.adminResponse
                ? "Atualizar resposta"
                : "Responder ao usuário"
            }
            hint="A resposta gera notificação pra o usuário e fica visível no painel dele."
          >
            <textarea
              value={responseText}
              onInput={(e) =>
                setResponseText((e.target as HTMLTextAreaElement).value)
              }
              maxLength={5000}
              rows={6}
              disabled={busy}
              placeholder="Ex: Obrigado pelo report! Vamos avaliar e te dar um retorno em breve."
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <div class="mt-1 text-right text-[10px] tabular-nums text-muted-foreground/70">
              {responseText.length}/5000
            </div>

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <span class="text-xs font-semibold text-muted-foreground">
                Marcar como:
              </span>
              <PillSelect
                value={newStatus}
                options={[
                  { v: "in_progress", label: "Em andamento" },
                  { v: "resolved", label: "Resolvido" },
                  { v: "wontfix", label: "Não será feito" }
                ]}
                disabled={busy}
                onChange={(v) =>
                  setNewStatus(v as AdminUserReport["status"])
                }
              />
            </div>

            {err && (
              <p class="mt-2 text-xs text-destructive">{err}</p>
            )}

            <button
              type="button"
              onClick={() => void submitResponse()}
              disabled={busy || !responseText.trim()}
              class="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send width="1em" height="1em" />
              )}
              Enviar resposta
            </button>
          </Section>

          {report.adminResponse && (
            <Section title="Resposta anterior">
              <p class="whitespace-pre-line text-sm leading-relaxed text-foreground">
                {report.adminResponse}
              </p>
              <p class="mt-1 text-[11px] text-muted-foreground">
                Enviada por{" "}
                <strong>{report.adminName ?? "—"}</strong>
                {report.respondedAt && (
                  <> · {formatDate(report.respondedAt)}</>
                )}
              </p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  children
}: {
  title: string;
  hint?: string;
  children: any;
}) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-4">
      <header>
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {hint && (
          <p class="mt-0.5 text-[11px] text-muted-foreground/80">{hint}</p>
        )}
      </header>
      {children}
    </section>
  );
}

function FieldRow({ label, children }: { label: string; children: any }) {
  return (
    <div class="flex flex-wrap items-center gap-2">
      <span class="min-w-24 text-xs font-semibold text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function PillSelect({
  value,
  options,
  disabled,
  onChange
}: {
  value: string;
  options: { v: string; label: string }[];
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div class="flex flex-wrap gap-0.5 rounded-md bg-background/60 p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.v)}
          class={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
            value === o.v
              ? "bg-primary text-white"
              : "text-muted-foreground hover:bg-accent"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ContextBlock({ context }: { context: Record<string, unknown> }) {
  const items: { label: string; value: string }[] = [];
  if (typeof context.url === "string" && context.url) {
    items.push({ label: "URL", value: context.url });
  }
  if (typeof context.path === "string" && context.path) {
    items.push({ label: "Path", value: context.path });
  }
  if (typeof context.userAgent === "string" && context.userAgent) {
    items.push({ label: "Browser", value: context.userAgent });
  }
  if (context.viewport && typeof context.viewport === "object") {
    const v = context.viewport as Record<string, unknown>;
    if (typeof v.w === "number" && typeof v.h === "number") {
      items.push({ label: "Viewport", value: `${v.w}×${v.h}` });
    }
  }
  if (typeof context.version === "string" && context.version) {
    items.push({ label: "Versão", value: context.version });
  }
  if (items.length === 0) {
    return (
      <p class="text-xs italic text-muted-foreground">
        Sem contexto técnico.
      </p>
    );
  }
  return (
    <dl class="space-y-1">
      {items.map((it) => (
        <div key={it.label} class="flex items-start gap-2 text-xs">
          <dt class="min-w-16 font-semibold text-muted-foreground">{it.label}</dt>
          <dd class="break-all font-mono text-[11px] text-foreground">
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}
